import { BreadcrumbBar, type BreadcrumbItem, Breadcrumbs } from "@design";
import { type ReactNode, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useMobileHeaderSlot } from "./mobileHeaderSlot";

export interface Crumb {
  label: string;
  /** Omit only when there's nowhere to go (e.g. still loading). */
  to?: string;
}

export interface PageBreadcrumbsProps {
  /** Ancestors, outermost first (e.g. Build, Agents). */
  trail: Crumb[];
  /** The current page. */
  current: string;
  /** Page-wide actions, right-aligned. */
  actions?: ReactNode;
}

/**
 * The page's breadcrumb (docs/design/web/WEB_BREADCRUMBS_DESIGN.md). Every page
 * below a top-level destination renders exactly one. On desktop/tablet it's a bar
 * at the top of the page; on mobile it moves into the shell's top bar.
 */
export function PageBreadcrumbs({ trail, current, actions }: Readonly<PageBreadcrumbsProps>) {
  const { t } = useTranslation("shell");
  const navigate = useNavigate();
  const slot = useMobileHeaderSlot();
  const claim = slot?.claim;
  useLayoutEffect(() => claim?.(), [claim]);

  const items: BreadcrumbItem[] = trail.map(({ label, to }) =>
    to ? { label, href: to, onSelect: () => navigate(to) } : { label },
  );
  const props = { items, current, label: t("nav.breadcrumb") };

  if (slot) {
    if (!slot.element) return null;
    return createPortal(
      <>
        <Breadcrumbs {...props} />
        {actions && <div className="ml-auto flex shrink-0 items-center gap-1">{actions}</div>}
      </>,
      slot.element,
    );
  }
  return <BreadcrumbBar {...props} actions={actions} />;
}
